/**
 * useDebugPanelExpanded — localStorage-persisted toggle. Default = collapsed.
 * Storage key per scope memo: `pcb.debug.panel.expanded`.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'pcb.debug.panel.expanded';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === '1' || v === 'true';
  } catch { return false; }
}

export function useDebugPanelExpanded(): {
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  toggle: () => void;
} {
  const [expanded, setExpandedState] = useState<boolean>(() => readInitial());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0'); } catch { /* quota */ }
  }, [expanded]);

  const setExpanded = useCallback((v: boolean) => setExpandedState(v), []);
  const toggle = useCallback(() => setExpandedState((v) => !v), []);

  return { expanded, setExpanded, toggle };
}

const SECTION_KEY_PREFIX = 'pcb.debug.section.';

// Default: collapsed (Bill 2026-04-26 — debug panel was unreadable when all
// 12 sections expanded simultaneously). User clicks to expand; choice persists.
export function useSectionCollapsed(sectionId: string, defaultCollapsed = true): {
  collapsed: boolean;
  toggle: () => void;
} {
  const key = SECTION_KEY_PREFIX + sectionId;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultCollapsed;
    try {
      const v = window.localStorage.getItem(key);
      if (v === null) return defaultCollapsed;
      return v === '1' || v === 'true';
    } catch { return defaultCollapsed; }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(key, collapsed ? '1' : '0'); } catch { /* quota */ }
  }, [collapsed, key]);

  return { collapsed, toggle: () => setCollapsed((v) => !v) };
}
